import { useQuery } from '@tanstack/react-query'
import { useState, type ReactNode } from 'react'

import type {
  StorefrontMetricsResponse,
  StorefrontSummary,
} from '@aeris/contracts'
import { formatMoney, fromString } from '@aeris/money'
import {
  QueryState,
  Section,
  Select,
  StatTile,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableHeaderCell,
  TableRow,
} from '@aeris/ui'

import { fetchStorefrontMetrics } from '../lib/api.ts'
import { formatDayTime } from '../lib/date-format.ts'
import { formatPhone } from '../lib/phone-format.ts'

/**
 * As métricas da loja online — F226.
 *
 * O dono pediu: *"métrica de acesso do usuário, login, compras"*. O que esta
 * aba responde, em ordem de dinheiro:
 * - quem tentou entrar e não é cliente (vira lista de cadastro);
 * - o que procuraram e não acharam (vira compra);
 * - o que mais olham e põem no carrinho;
 * - os pedidos e quanto cada vendedora aprovou;
 * - quem são os clientes que entram.
 */

type Period = 'today' | '7' | '30'

const PERIODS: Record<Period, string> = {
  today: 'Hoje',
  '7': 'Últimos 7 dias',
  '30': 'Últimos 30 dias',
}

/** O dia de São Paulo, `n` dias atrás, em AAAA-MM-DD. */
const dayInSaoPaulo = (daysAgo: number): string =>
  new Date(Date.now() - 3 * 3_600_000 - daysAgo * 24 * 3_600_000)
    .toISOString()
    .slice(0, 10)

const periodOf = (period: Period): { from: string; to: string } => ({
  from: dayInSaoPaulo(period === 'today' ? 0 : Number(period) - 1),
  to: dayInSaoPaulo(0),
})

const money = (value: string): string => formatMoney(fromString(value))

const when = (iso: string | null): string =>
  iso === null ? '—' : formatDayTime(iso)

/** "5533912345678" → "(33) 91234-5678" — o formatador da casa (F248). */
const phone = (digits: string): string => formatPhone(digits)

export const StorefrontMetrics = ({
  store,
}: {
  readonly store: StorefrontSummary
}): ReactNode => {
  const [period, setPeriod] = useState<Period>('7')
  const range = periodOf(period)

  const metrics = useQuery({
    queryKey: ['storefront-metrics', store.id, range.from, range.to],
    queryFn: async ({ signal }) =>
      await fetchStorefrontMetrics(store.id, range, signal),
  })

  return (
    <Section
      title="Métricas"
      description="Quem entrou, o que procurou, o que pôs no carrinho e o que virou pedido."
      action={
        <Select
          aria-label="Período"
          className="w-44"
          value={period}
          onChange={(event) => {
            setPeriod(event.target.value as Period)
          }}
        >
          {(Object.keys(PERIODS) as Period[]).map((key) => (
            <option key={key} value={key}>
              {PERIODS[key]}
            </option>
          ))}
        </Select>
      }
    >
      <QueryState
        isPending={metrics.isPending}
        isError={metrics.isError}
        error={metrics.error}
        onRetry={() => void metrics.refetch()}
        what="as métricas"
        isEmpty={false}
        empty={null}
      >
        {metrics.data === undefined ? null : <Report data={metrics.data} />}
      </QueryState>
    </Section>
  )
}

const Report = ({
  data,
}: {
  readonly data: StorefrontMetricsResponse
}): ReactNode => (
  <div className="flex flex-col gap-6">
    <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
      <StatTile label="Visitantes" value={String(data.visitors)} />
      <StatTile
        label="Clientes que entraram"
        value={String(data.customersIn)}
        hint={`${String(data.logins)} entradas`}
      />
      <StatTile
        label="Pedidos"
        value={String(data.orders.placed)}
        hint={`${String(data.orders.approved)} aprovados · ${String(data.orders.pending)} esperando`}
      />
      <StatTile
        label="Aprovado"
        value={money(data.orders.approvedAmount)}
        hint={`pedido: ${money(data.orders.placedAmount)}`}
      />
    </div>

    <Block
      title="Procuraram e não acharam"
      empty="Toda busca do período achou alguma coisa."
      headers={['Busca', 'Vezes', 'Última']}
      rows={data.zeroResultTerms.map((row) => [
        row.term,
        String(row.count),
        when(row.lastAt),
      ])}
    />
    <Block
      title="Tentaram entrar sem cadastro"
      empty="Ninguém tentou entrar com número sem cadastro."
      headers={['Número', 'Tentativas', 'Última']}
      rows={data.refusedLogins.map((row) => [
        phone(row.phone),
        String(row.attempts),
        when(row.lastAt),
      ])}
    />
    <div className="grid gap-6 lg:grid-cols-2">
      <Block
        title="Mais vistos"
        empty="Nenhum produto aberto no período."
        headers={['Produto', 'Vezes']}
        rows={data.topViewed.map((row) => [
          row.name ?? row.code,
          String(row.count),
        ])}
      />
      <Block
        title="Mais postos no carrinho"
        empty="Nada foi para o carrinho no período."
        headers={['Produto', 'Vezes', 'Peças']}
        rows={data.topAdded.map((row) => [
          row.name ?? row.code,
          String(row.count),
          String(row.quantity),
        ])}
      />
    </div>
    <Block
      title="Mais procurados"
      empty="Nenhuma busca no período."
      headers={['Busca', 'Vezes', 'Resultados']}
      rows={data.topTerms.map((row) => [
        row.term,
        String(row.count),
        String(row.results),
      ])}
    />
    <Block
      title="Por vendedora"
      empty="Nenhum pedido no período."
      headers={['Vendedora', 'Pedidos', 'Aprovados', 'Aprovado']}
      rows={data.bySeller.map((row) => [
        row.sellerName ?? 'Sem vendedora',
        String(row.placed),
        String(row.approved),
        money(row.approvedAmount),
      ])}
    />
    <Block
      title="Clientes"
      empty="Nenhum cliente entrou no período."
      headers={[
        'Cliente',
        'Entradas',
        'Último acesso',
        'Buscas',
        'Carrinho',
        'Pedidos',
        'Valor',
      ]}
      rows={data.customers.map((row) => [
        row.code === null ? row.name : `${row.name} (${row.code})`,
        String(row.logins),
        when(row.lastSeenAt),
        String(row.searches),
        String(row.addedToCart),
        String(row.orders),
        money(row.ordersAmount),
      ])}
    />
  </div>
)

/** Uma tabela pequena da aba, com o que dizer quando vazia. */
const Block = ({
  title,
  empty,
  headers,
  rows,
}: {
  readonly title: string
  readonly empty: string
  readonly headers: readonly string[]
  readonly rows: readonly (readonly string[])[]
}): ReactNode => (
  <div className="flex flex-col gap-2">
    <h4 className="text-(length:--text-sm) font-(--weight-medium)">{title}</h4>
    {rows.length === 0 ? (
      <p className="text-(length:--text-sm) text-(--text-secondary)">{empty}</p>
    ) : (
      <TableContainer>
        <Table aria-label={title}>
          <TableHead>
            <TableRow>
              {headers.map((header, index) => (
                <TableHeaderCell key={header} numeric={index > 0}>
                  {header}
                </TableHeaderCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.map((row) => (
              <TableRow key={row.join('|')}>
                {row.map((cell, index) => (
                  <TableCell key={headers[index] ?? ''} numeric={index > 0}>
                    {cell}
                  </TableCell>
                ))}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    )}
  </div>
)
